import { type SignatureProcessAPIApiAddDocumentRequest } from '@cencert/mob-signature'

export type DocumentSignatureFormat =
  SignatureProcessAPIApiAddDocumentRequest['documentSignFormat']

export interface SignatureTypeConfig {
  value: string
  documentSignFormat: DocumentSignatureFormat
  allowedFileTypes: string[]
  requiresCode: boolean
  isFree: boolean
}

export const SIGNATURE_TYPES: SignatureTypeConfig[] = [
  {
    value: 'freeSignature',
    documentSignFormat: 'PAdES',
    allowedFileTypes: ['.pdf'],
    requiresCode: false,
    isFree: true,
  },
  {
    value: 'PAdES',
    documentSignFormat: 'PAdES',
    allowedFileTypes: ['.pdf'],
    requiresCode: true,
    isFree: false,
  },
  {
    value: 'XAdES',
    documentSignFormat: 'XAdES',
    allowedFileTypes: ['.xml'],
    requiresCode: true,
    isFree: false,
  },
  {
    value: 'XAdES_DETACHED',
    documentSignFormat: 'XAdES_DETACHED',
    allowedFileTypes: [], // Can be used with any file type
    requiresCode: true,
    isFree: false,
  },
  {
    value: 'CAdES',
    documentSignFormat: 'CAdES',
    allowedFileTypes: [], // Can be used with any file type
    requiresCode: true,
    isFree: false,
  },
  {
    value: 'CAdES_DETACHED',
    documentSignFormat: 'CAdES_DETACHED',
    allowedFileTypes: [], // Can be used with any file type
    requiresCode: true,
    isFree: false,
  },
]

/**
 * Maps signature type value to documentSignFormat for API calls
 */
export function getDocumentSignFormat(
  signatureType: string
): DocumentSignatureFormat {
  const config = SIGNATURE_TYPES.find(type => type.value === signatureType)
  return config?.documentSignFormat ?? 'PAdES' // Default to PAdES for free signature
}

/**
 * Gets signature type configuration by value
 */
export function getSignatureTypeConfig(
  signatureType: string
): SignatureTypeConfig | undefined {
  return SIGNATURE_TYPES.find(type => type.value === signatureType)
}

/**
 * Validates file type against signature format requirements
 */
export function validateFileForSignatureType(
  file: File,
  signatureType: string,
  t: (key: string) => string
): string | null {
  const config = getSignatureTypeConfig(signatureType)

  if (!config) {
    return null // Unknown signature type, skip validation
  }

  // If no specific file types are required, allow any file
  if (config.allowedFileTypes.length === 0) {
    return null
  }

  const fileName = file.name.toLowerCase()
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'))
  const mimeType = file.type.toLowerCase()

  const isValidFileType = config.allowedFileTypes.some(allowedType => {
    if (allowedType === '.pdf') {
      return fileExtension === '.pdf' || mimeType.includes('pdf')
    }
    if (allowedType === '.xml') {
      return fileExtension === '.xml' || mimeType.includes('xml')
    }
    return fileExtension === allowedType
  })

  if (!isValidFileType) {
    if (config.documentSignFormat === 'PAdES') {
      return t('documentAdd.errors.pdfRequired')
    }
    if (config.documentSignFormat === 'XAdES') {
      return t('documentAdd.errors.xmlRequired')
    }
  }

  return null
}

/**
 * Checks if signature type requires a signature code
 */
export function requiresSignatureCode(signatureType: string): boolean {
  const config = getSignatureTypeConfig(signatureType)
  return config?.requiresCode ?? false
}

/**
 * Checks if signature type is free
 */
export function isFreeSignatureType(signatureType: string): boolean {
  const config = getSignatureTypeConfig(signatureType)
  return config?.isFree ?? false
}
